/**
 * SlimSelect 래퍼 – 기존 <select> DOM에 SlimSelect 적용.
 * 컨트롤러에서는 new SelectWrapper(selector, options).init() 만 사용.
 */
declare global {
  interface Window {
    SlimSelect?: new (config: SlimSelectConfig) => SlimSelectInstance;
  }
}

interface SlimSelectConfig {
  select: string | HTMLSelectElement;
  settings?: Record<string, unknown>;
  events?: Record<string, (...args: unknown[]) => void>;
}

interface SlimSelectInstance {
  destroy?: () => void;
}

export interface SelectWrapperOptions {
  placeholder?: string;
  searchPlaceholder?: string;
  openPosition?: string;
}

export class SelectWrapper {
  private readonly target: string | HTMLSelectElement;
  private readonly options: SelectWrapperOptions;
  private instance: SlimSelectInstance | null = null;

  constructor(
    target: string | HTMLSelectElement,
    options: SelectWrapperOptions = {}
  ) {
    this.target = target;
    this.options = options;
  }

  init(): SlimSelectInstance | null {
    const SlimSelect = typeof window !== "undefined" ? window.SlimSelect : undefined;
    if (!SlimSelect) return null;

    const el =
      typeof this.target === "string"
        ? document.querySelector<HTMLSelectElement>(this.target)
        : this.target;
    if (!el) return null;

    if (el.hasAttribute("data-ss-inited")) return this.instance;

    const placeholder =
      el.getAttribute("data-placeholder") ??
      this.options.placeholder ??
      (el.options[0]?.value === "" ? el.options[0].text : null) ??
      "선택하세요";

    try {
      this.instance = new SlimSelect({
        select: el,
        settings: {
          placeholderText: placeholder,
          searchPlaceholder: this.options.searchPlaceholder ?? "검색...",
          openPosition: (this.options.openPosition as "auto" | "up" | "down") ?? "auto",
          // 드롭다운 너비: 옵션 텍스트에 맞춤 (input 너비가 아님)
          contentWidth: "max-content",
        },
      });
      el.setAttribute("data-ss-inited", "1");
    } catch {
      return null;
    }
    return this.instance;
  }

  getInstance(): SlimSelectInstance | null {
    return this.instance;
  }

  destroy(): void {
    if (this.instance?.destroy) {
      this.instance.destroy();
      this.instance = null;
    }
    const el =
      typeof this.target === "string"
        ? document.querySelector<HTMLSelectElement>(this.target)
        : this.target;
    if (el) el.removeAttribute("data-ss-inited");
  }
}
